import React, { useState, useRef, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Footer from "../../components/Footer";
import { dataAnalyticsFeatures } from "./dataAnalyticsFeatures";

// --- ★ UPDATED StickyTabNav (Wrapper Strategy for Perfect Stability) ★ ---
const StickyTabNav = ({ features, activeTab, onTabClick, mainNavHeight }) => {
  const containerRef = useRef(null); // Measure this wrapper, NOT the nav directly
  const activeTabRef = useRef(null);

  const [isSticky, setIsSticky] = useState(false);
  const [originalTop, setOriginalTop] = useState(0);
  const [navHeight, setNavHeight] = useState(0);

  // 1. ROBUST MEASUREMENT SYSTEM
  useEffect(() => {
    const updatePosition = () => {
      if (containerRef.current) {
        const top = containerRef.current.offsetTop;
        const height = containerRef.current.offsetHeight;

        if (Math.abs(top - originalTop) > 5) {
          setOriginalTop(top);
        }
        if (height > 0 && Math.abs(height - navHeight) > 2) {
          setNavHeight(height);
        }
      }
    };

    updatePosition();
    const timer1 = setTimeout(updatePosition, 200);
    const timer2 = setTimeout(updatePosition, 1000);
    const timer3 = setTimeout(updatePosition, 3000);

    window.addEventListener("resize", updatePosition);

    const resizeObserver = new ResizeObserver(() => updatePosition());
    if (containerRef.current) resizeObserver.observe(containerRef.current);
    resizeObserver.observe(document.body);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      window.removeEventListener("resize", updatePosition);
      resizeObserver.disconnect();
    };
  }, [originalTop, navHeight]);

  // 2. SCROLL LOGIC
  useEffect(() => {
    const handleScroll = () => {
      const safeNavHeight = mainNavHeight || 0;

      if (window.scrollY < 10) {
        setIsSticky(false);
        return;
      }

      if (originalTop === 0) return;

      const threshold = originalTop - safeNavHeight;
      const shouldBeSticky = window.scrollY >= threshold;

      if (shouldBeSticky !== isSticky) {
        setIsSticky(shouldBeSticky);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [originalTop, isSticky, mainNavHeight]);

  // 3. Auto-Center Active Tab
  useEffect(() => {
    if (activeTabRef.current) {
      activeTabRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [activeTab]);

  const stickyStyles = {
    transform: "translateZ(0)",
  };

  if (isSticky) {
    stickyStyles.top = `${mainNavHeight || 0}px`;
  }

  return (
    <>
      <style>
        {`
          .hide-scrollbar::-webkit-scrollbar { display: none; }
          .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        `}
      </style>

      {/* WRAPPER: This div tracks the correct position in the document flow */}
      <div ref={containerRef} className="relative w-full z-20">
        <nav
          className={`transition-[background,box-shadow,padding] duration-300 ease-in-out w-full ${
            isSticky
              ? "fixed left-0 right-0 bg-white/95 backdrop-blur-md shadow-lg"
              : "relative bg-white/95 shadow-none"
          }`}
          style={isSticky ? stickyStyles : {}}
        >
          <div className="container mx-auto max-w-6xl px-0">
            <div className="flex overflow-x-auto hide-scrollbar">
              {features.map((feature) => (
                <button
                  key={feature.id}
                  ref={activeTab === feature.id ? activeTabRef : null}
                  onClick={() => onTabClick(feature.id)}
                  className={`
                    relative px-6 md:px-8 text-left font-medium text-base
                    transition-colors duration-300 flex-1
                    flex items-start justify-start
                    min-w-[160px] 
                    py-2 h-20 
                    ${
                      activeTab === feature.id
                        ? "bg-gray-100 text-gray-800"
                        : "bg-white text-gray-700 hover:bg-gray-50"
                    }
                  `}
                >
                  {feature.title}
                  {activeTab === feature.id && (
                    <motion.div
                      className="absolute bottom-0 left-0 right-0 h-1.5 bg-green-900"
                      layoutId="underline"
                    />
                  )}
                </button>
              ))}
            </div>
          </div>
        </nav>

        {/* Placeholder: Keeps the height open when Nav becomes fixed */}
        {isSticky && <div style={{ height: `${navHeight || 80}px` }} />}
      </div>
    </>
  );
};

// --- FeatureSection component (Standard Design) ---
const FeatureSection = React.forwardRef(({ feature }, ref) => (
  <div ref={ref} className="container mx-auto max-w-6xl px-4 pt-16 md:pt-24">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center min-h-[450px]">
      {/* Left Side (Text) */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.5 }}
      >
        {feature.icon && <div className="mb-6">{feature.icon}</div>}
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          {feature.heading}
        </h2>

        <ul className="my-8 p-0 list-none">
          {feature.points.map((point, index) => (
            <li
              key={index}
              className="py-2 border-b border-gray-200 last:border-b-0"
            >
              <span className="text-gray-600 text-sm leading-relaxed font-medium block text-left">
                {point}
              </span>
            </li>
          ))}
        </ul>
        <Link
          to={`/data-analytics/${feature.id}`}
          className="inline-block bg-green-900 text-white font-semibold mt-2 text-lg px-6 py-2 rounded-md hover:bg-green-700 transition-colors no-underline"
        >
          More Information &rarr;
        </Link>
      </motion.div>

      {/* Right Side (Image) */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.5 }}
        className="flex items-center justify-center md:justify-end"
      >
        <div className="relative overflow-hidden rounded-2xl group">
          <img
            src={feature.imageUrl}
            alt={feature.heading}
            className="w-full h-auto max-w-md object-contain rounded-2xl transition-transform duration-700 ease-out group-hover:scale-[1.15]"
            draggable={false}
          />
        </div>
      </motion.div>
    </div>
  </div>
));

// --- Main Component ---
export default function DataAnalytics() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [activeTab, setActiveTab] = useState(dataAnalyticsFeatures[0].id);
  const [mainNavHeight, setMainNavHeight] = useState(null);

  useEffect(() => {
    const updateNavHeight = () => {
      const mainNav = document.getElementById("main-navbar");
      if (mainNav) {
        setMainNavHeight(mainNav.offsetHeight);
      }
    };
    updateNavHeight();
    window.addEventListener("resize", updateNavHeight);
    return () => window.removeEventListener("resize", updateNavHeight);
  }, []);

  const sectionRefs = useRef(
    dataAnalyticsFeatures.reduce((acc, feature) => {
      acc[feature.id] = React.createRef();
      return acc;
    }, {}),
  );

  useEffect(() => {
    const safeHeight = mainNavHeight || 0;
    // ★ FIXED OFFSET: safeHeight + 100 (matches compact nav)
    const offset = safeHeight + 100;

    const handleScroll = () => {
      let currentTab = activeTab;

      for (const feature of dataAnalyticsFeatures) {
        const ref = sectionRefs.current[feature.id];
        if (ref.current) {
          const { top, bottom } = ref.current.getBoundingClientRect();
          if (top <= offset && bottom >= offset) {
            currentTab = feature.id;
            break;
          }
        }
      }

      if (currentTab !== activeTab) {
        setActiveTab(currentTab);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [activeTab, mainNavHeight]);

  const handleTabClick = (id) => {
    const ref = sectionRefs.current[id];
    const safeHeight = mainNavHeight || 0;

    if (ref.current) {
      // ★ FIXED SCROLL TO: -80px (matches compact nav)
      const y =
        ref.current.getBoundingClientRect().top +
        window.scrollY -
        safeHeight -
        80;
      window.scrollTo({ top: y, behavior: "smooth" });
      setActiveTab(id);
    }
  };

  return (
    <>
      <Helmet>
        <title>
          Data Analytics & AI-Powered Observability | Synthera Security
        </title>

        <meta
          name="description"
          content="Gain visibility across applications, infrastructure, and data with AI-powered observability and data analytics solutions from Synthera Security."
        />

        <meta
          name="keywords"
          content="Data Analytics, Observability, AI Analytics, Security Analytics, Monitoring, Infrastructure Visibility, Synthera Security"
        />

        <meta name="robots" content="index, follow" />

        <meta
          property="og:title"
          content="Data Analytics & AI-Powered Observability | Synthera Security"
        />

        <meta
          property="og:description"
          content="See inside any stack, any application, and any environment with AI-powered observability and analytics."
        />

        <meta
          property="og:url"
          content="https://www.syntherasecurity.com/solutions/data-analytics"
        />

        <meta property="og:type" content="website" />

        <link
          rel="canonical"
          href="https://www.syntherasecurity.com/solutions/data-analytics"
        />
      </Helmet>

      <div className="bg-white text-gray-900 overflow-hidden">
        {/* 1. DATA ANALYTICS HERO SECTION */}
        <div className="w-full bg-[#032d28]">
          {/* Desktop */}
          <div className="hidden md:block">
            <img
              src="https://res.cloudinary.com/dpn3bkjba/image/upload/v1781090762/opertional_bujl48.png"
              alt="Data Analytics"
              className="w-full h-auto block"
            />
          </div>

          {/* Mobile */}
          <div className="block md:hidden pt-16 overflow-hidden">
            <img
              src="https://res.cloudinary.com/dpn3bkjba/image/upload/v1781090762/opertional_bujl48.png"
              alt="Data Analytics"
              className="w-full h-auto block"
            />
          </div>
        </div>

        <section className="w-full bg-[#ffffff] py-28 px-6">
          <div className="max-w-7xl mx-auto text-center">
            {/* Heading */}
            <h2 className="text-4xl md:text-6xl font-semibold text-gray-900">
              AI-Powered Observability and Security
            </h2>
            {/* Subtitle */}
            <p className="text-lg text-gray-700 mt-4">
              See inside any stack, any app, at any scale, anywhere.
            </p>
            {/* Buttons */}
            {/* <div className="flex justify-center gap-4 mt-8">
            <button className="bg-purple-700 text-white px-8 py-3 rounded-md font-semibold hover:bg-purple-800 transition">
              FREE TRIAL
            </button>

            <button className="border border-purple-700 text-purple-700 px-8 py-3 rounded-md font-semibold hover:bg-purple-50 transition">
              ▶ SEE THE PLATFORM
            </button>
          </div> */}
            {/* Video */}
            <div className="mt-16 flex justify-center">
              <video
                className="w-full max-w-6xl"
                autoPlay
                muted
                loop
                playsInline
                poster="https://res.cloudinary.com/dpn3bkjba/image/upload/f_auto,q_auto:good,w_1600/v1772701522/dd-homepage_muyn0n.avif"
              >
                <source
                  src="https://res.cloudinary.com/dpn3bkjba/video/upload/f_auto,q_auto:good,w_1600/v1772696872/homepage-animation-1080_pvzoyz.mp4"
                  type="video/mp4"
                />
              </video>
            </div>
          </div>
        </section>

        {/* --- 5. STICKY NAV (Updated with Wrapper Fix) --- */}
        <StickyTabNav
          features={dataAnalyticsFeatures}
          activeTab={activeTab}
          onTabClick={handleTabClick}
          mainNavHeight={mainNavHeight}
        />

        {/* --- 6. FEATURE SECTIONS --- */}
        <div className="pb-16 md:pb-24">
          {dataAnalyticsFeatures.map((feature, index) => (
            <React.Fragment key={feature.id}>
              {index > 0 && (
                <div className="container mx-auto max-w-6xl px-4">
                  <hr className="border-t-1 border-gray-900" />
                </div>
              )}
              <FeatureSection
                feature={feature}
                ref={sectionRefs.current[feature.id]}
              />
            </React.Fragment>
          ))}
        </div>
        <Footer />
      </div>
    </>
  );
}
